import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Modal,
    Typography,
    Input,
    DatePicker,
    Radio,
    Flex,
    Button,
    Alert,
} from '@app/ui';
import { getScheduleExecutionTime } from '@app/utils/getScheduleExecutionTime';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import { useScheduleReport } from './hooks/useScheduleReport';
import hasRequiredSelection from './hasRequiredSelection';
import './ScheduleModal.scss';

const { Text } = Typography;

interface ScheduleModalV2Props {
    open: boolean;
    onCancel: () => void;
    scheduleHookConfig: any;
    editMode?: boolean;
    editScheduleData?: any;
    rightsholderAttribution?: string;
}

const ScheduleModalV2: React.FC<ScheduleModalV2Props> = ({
    open,
    onCancel,
    scheduleHookConfig,
    editMode = false,
    editScheduleData = null,
    rightsholderAttribution = null,
}) => {
    const { t } = useTranslation('tools');
    const { values, setFieldValue } = useFormikContext<any>();
    const [testSuccess, setTestSuccess] = useState(false);
    const [isScheduling, setIsScheduling] = useState(false);

    const { sendSchedule, sendTest } = useScheduleReport({
        values,
        ...scheduleHookConfig,
        rightsholderAttribution,
    });

    // Populate form fields when opening in edit mode
    useEffect(() => {
        if (open && editMode && editScheduleData) {
            setFieldValue(
                'scheduleReportName',
                editScheduleData.mvcReportName || '',
                false,
            );
            setFieldValue(
                'scheduleReportCadence',
                editScheduleData.cadence || undefined,
                false,
            );
            const schedulePayloadData = JSON.parse(
                JSON.parse(editScheduleData.httpRequestData).requestPayload,
            ).data;

            setFieldValue(
                'scheduleReportStartDate',
                schedulePayloadData.startDate
                    ? dayjs(schedulePayloadData.startDate)
                    : null,
                false,
            );
        }
    }, [open, editMode, editScheduleData, setFieldValue]);

    const nextExecutionDate = useMemo(() => {
        const date = getScheduleExecutionTime({
            startDate: values.scheduleReportStartDate || values.startDate,
            cadence: values.scheduleReportCadence,
            dayOfMonth: 10,
        });
        return new Date(date).toLocaleDateString(window.navigator.language, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    }, [
        values.scheduleReportCadence,
        values.scheduleReportStartDate,
        values.startDate,
    ]);

    const isFormComplete = Boolean(
        values.scheduleReportName &&
            values.scheduleReportStartDate &&
            values.scheduleReportCadence &&
            (editMode || hasRequiredSelection(values)),
    );

    const handleClose = () => {
        setFieldValue('scheduleReportName', '', false);
        setFieldValue('scheduleReportStartDate', null, false);
        setFieldValue('scheduleReportCadence', undefined, false);
        setTestSuccess(false);
        onCancel();
    };

    const handleSendTest = async () => {
        if (await sendTest()) {
            setTestSuccess(true);
        }
    };

    const handleSchedule = async () => {
        setIsScheduling(true);
        const scheduled = await sendSchedule();
        setIsScheduling(false);
        // keep the modal (and what the user typed) open if the schedule was not saved
        if (scheduled) {
            handleClose();
        }
    };

    const modalTitle = editMode ? t('reports.modals.schedule.titleEdit') : t('reports.modals.schedule.titleCreate');
    const mainButtonText = editMode ? t('reports.buttons.updateSchedule') : t('reports.buttons.scheduleReport');

    return (
        <Modal
            title={modalTitle}
            open={open}
            onCancel={handleClose}
            footer={null}
            width={520}
            centered
            destroyOnClose
            afterClose={handleClose}
            className="schedule-modal-v2"
        >
            <div className="schedule-modal-v2__field schedule-modal-v2__field--name">
                <Text className="schedule-modal-v2__label">{t('reports.modals.schedule.scheduleName')}</Text>
                <Input
                    placeholder={t('reports.modals.schedule.scheduleNamePlaceholder')}
                    value={values.scheduleReportName || ''}
                    onChange={e =>
                        setFieldValue(
                            'scheduleReportName',
                            e.target.value,
                            false,
                        )
                    }
                />
            </div>

            <div className="schedule-modal-v2__field schedule-modal-v2__field--date">
                <Text className="schedule-modal-v2__label">
                    {t('reports.modals.schedule.includeDataAfter')}
                </Text>
                <DatePicker
                    value={
                        values.scheduleReportStartDate
                            ? dayjs(values.scheduleReportStartDate)
                            : null
                    }
                    onChange={date =>
                        setFieldValue('scheduleReportStartDate', date, false)
                    }
                    style={{ width: '100%' }}
                />
                <Text type="secondary" className="schedule-modal-v2__info">
                    {t('reports.modals.schedule.includeDataAfterInfo')}
                </Text>
            </div>

            <div className="schedule-modal-v2__field schedule-modal-v2__field--frequency">
                <Text className="schedule-modal-v2__label">{t('reports.modals.schedule.frequency')}</Text>
                <Radio.Group
                    value={values.scheduleReportCadence}
                    onChange={e =>
                        setFieldValue(
                            'scheduleReportCadence',
                            e.target.value,
                            false,
                        )
                    }
                    style={{ width: '100%' }}
                >
                    <Flex gap={16}>
                        <Radio value="weekly">{t('reports.modals.schedule.weeklyOption')}</Radio>
                        <Radio value="monthly">
                            {t('reports.modals.schedule.monthlyOption')}
                        </Radio>
                    </Flex>
                </Radio.Group>
                <Text className="schedule-modal-v2__next-execution">
                    {t('reports.modals.schedule.firstReportSentOn')}
                    <Text strong style={{ marginLeft: 4, color: '#1677ff' }}>
                        {nextExecutionDate}
                    </Text>
                </Text>
            </div>

            {testSuccess && (
                <Alert
                    className="schedule-modal-v2__alert"
                    style={{ marginBottom: 16 }}
                    message={t('reports.messages.success.testSent')}
                    type="success"
                    showIcon
                />
            )}

            <Flex
                justify="flex-end"
                gap={8}
                className="schedule-modal-v2__footer"
            >
                <Button
                    onClick={handleClose}
                    style={{ minWidth: 80 }}
                    icon={<i className="fa-regular fa-xmark -m-r-2" />}
                    type="primary"
                    color="default"
                    variant="solid"
                >
                    {t('reports.buttons.cancel')}
                </Button>
                <Button
                    type="default"
                    style={{ minWidth: 110 }}
                    icon={<i className="fa-regular fa-envelope -m-r-2" />}
                    onClick={handleSendTest}
                    color="primary"
                    variant="outlined"
                    disabled={!isFormComplete}
                >
                    {t('reports.buttons.sendTest')}
                </Button>
                <Button
                    type="primary"
                    style={{ minWidth: 140 }}
                    icon={<i className="fa-regular fa-calendar -m-r-2" />}
                    onClick={handleSchedule}
                    variant="solid"
                    disabled={!isFormComplete || isScheduling}
                    loading={isScheduling}
                >
                    {mainButtonText}
                </Button>
            </Flex>
        </Modal>
    );
};

export default ScheduleModalV2;
